// src/apollo/retryRefreshLink.js

import { ApolloLink, Observable } from "@apollo/client";
import { performRefresh } from "../utils/refreshToken";
import { authStore } from "../utils/tokenStore";
import { logOut } from "../utils/functions";

const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log(...args);
  }
};

const looksLikeJwtProblem = (msg = "") => {
  const m = msg.toLowerCase();
  return (
    m.includes("expired token") ||
    m.includes("invalid-secret-key") ||
    m.includes("invalid token") ||
    m.includes("jwt") ||
    m.includes("signature")
  );
};

/**
 * Apollo retry link — FINAL version
 */
export const retryRefreshLink = (getClient) =>
  new ApolloLink((operation, forward) => {
    const context = operation.getContext();

    // Do not retry refreshToken mutation
    if (context.skipRetryLink) {
      devLog("⏭️ retryRefreshLink: skipping refresh mutation");
      return forward(operation);
    }

    return new Observable((observer) => {
      let subscription;

      const runOperation = () => {
        subscription = forward(operation).subscribe({
          // -----------------------------------------------------
          // SUCCESSFUL RESPONSE (HTTP 200) — but may contain GraphQL errors
          // -----------------------------------------------------
          next: async (result) => {
            devLog("📦 retryRefreshLink → next() result:", result);

            const errorList = result?.errors || [];
            if (errorList.length > 0) {
              const errorText = JSON.stringify(errorList).toLowerCase();
              devLog("🧩 GraphQL response errors:", errorText);

              if (looksLikeJwtProblem(errorText)) {
                devLog("🔴 JWT error inside response → attempting refresh");

                const client = getClient();
                const newToken = await performRefresh(client);

                if (!newToken) {
                  devLog("🔥 Refresh failed → logout");
                  authStore.clear();
                  logOut();
                  observer.error(errorList);
                  return;
                }

                devLog("🔄 Refresh succeeded → retrying operation");

                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${newToken}`,
                  },
                }));

                forward(operation).subscribe(observer);
                return;
              }
            }

            observer.next(result);
          },

          // -----------------------------------------------------
          // NETWORK ERROR (actual failure)
          // -----------------------------------------------------
          error: async (error) => {
            devLog("❗ retryRefreshLink → network error:", error);

            const msg = (error?.message || "").toLowerCase();

            if (looksLikeJwtProblem(msg)) {
              devLog("🔴 Network-level JWT failure → logout");
              authStore.clear();
              logOut();
            }

            observer.error(error);
          },

          // -----------------------------------------------------
          complete: () => observer.complete(),
        });
      };

      runOperation();

      return () => subscription?.unsubscribe();
    });
  });
